import { CERTIFICATE_TEMPLATES, truncateName } from './templates';
import { certificateSrc } from './CertificateArt';
import { usd } from '../../../utilities/format';
import { saveBlob } from '../../../utilities/download';

const fieldText = (key, item) => {
  if (key === 'name') return truncateName(item?.name);
  if (key === 'amount') return usd(item?.amount, { decimals: 2 });
  return item?.created_at ?? '';
};

const safeFileName = (name) =>
  String(name || 'certificate')
    .trim()
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, '_') || 'certificate';

/**
 * Downloads the rendered certificate matching what the user sees in preview.
 * Draws the high-resolution artwork template and lays over the trader's name,
 * date, and amount at exact coordinates matching CertificateArt.
 */
export const downloadRenderedCertificate = async (item) => {
  if (!item) return;

  const template = CERTIFICATE_TEMPLATES[item.type];

  // If this type has no local template, fallback to the image at item.link
  if (!template) {
    const src = certificateSrc(item.link);
    if (!src) return;
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      saveBlob(blob, `${safeFileName(item.name || item.title)}_certificate.png`);
    } catch {
      const a = document.createElement('a');
      a.href = src;
      a.download = `${safeFileName(item.name || item.title)}_certificate.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    return;
  }

  // Ensure custom web fonts are loaded
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch (e) {
      // Best-effort font loading
    }
  }

  // Load the template image
  const img = new Image();
  img.crossOrigin = 'anonymous';

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = template.image;
  });

  const width = img.naturalWidth || 1968;
  const height = img.naturalHeight || 1487;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Draw background artwork
  ctx.drawImage(img, 0, 0, width, height);

  // Draw each text field
  for (const field of template.fields) {
    const text = fieldText(field.key, item);
    if (!text) continue;

    const fontSize = (field.size / 100) * width;
    const x = (field.left / 100) * width;

    // Line height is 1.19 in CertificateArt.scss
    const fieldHeight = fontSize * 1.19;

    let y;
    if (field.top != null) {
      y = (field.top / 100) * height;
    } else {
      y = (1 - field.bottom / 100) * height - fieldHeight;
    }

    const weight = field.weight ? `${field.weight} ` : '';
    const font = field.font || 'sans-serif';

    ctx.font = `${weight}${Math.round(fontSize)}px ${font}`;
    ctx.fillStyle = field.color || '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const maxW = field.width ? (field.width / 100) * width : undefined;
    if (maxW) {
      ctx.fillText(text, x, y, maxW);
    } else {
      ctx.fillText(text, x, y);
    }
  }

  // Trigger download of the canvas as PNG
  const fileName = `${safeFileName(item.name || item.title)}_certificate.png`;

  if (canvas.toBlob) {
    canvas.toBlob((blob) => {
      if (blob) {
        saveBlob(blob, fileName);
      } else {
        const dataUrl = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    }, 'image/png');
  } else {
    const dataUrl = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
};
